
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import ToggleButton from '@/ui-components/ToggleButton.vue';
import { PlotRecommenderState } from './plot-recommender.store';
import { AppDirection } from '@/enums/app-direction';

const LpPlotRecommender = namespace('LpPlotRecommender');
const Meta = namespace('meta');

@Component({
	name: 'PlotRecommenderPriceFilter',
	components: { ToggleButton },
})
export default class PlotRecommenderPriceFilter extends Vue {
	@LpPlotRecommender.State filters: PlotRecommenderState['filters'];
	@LpPlotRecommender.Mutation SET_FILTER: Function;

	private max = 50 * this.currencyAmplifier;
	price = {
		min: 0,
		max: this.max,
		step: 1,
		value: {
			lower: 0,
			upper: this.max,
		},
	};

	@Meta.Getter appDirection: AppDirection;

	get isRTL(): boolean {
		return this.appDirection === AppDirection.RTL;
	}

	get currencyAmplifier() {
		return 1;
	}

	/**
	 * min_price and max_price are used to send it to the filter route.
	 */
	get presets() {
		return [1, 2, 3, 5]
			.map(item => ({
				min_price: 0,
				max_price: (item * this.currencyAmplifier) as number | null,
				label: 'WEBAPP_PRICEFILTER_UPTO',
				value: item * this.currencyAmplifier,
			}))
			.concat({
				min_price: 5 * this.currencyAmplifier,
				max_price: null,
				label: 'WEBAPP_PRICEFILTER_MORETHAN',
				value: 5 * this.currencyAmplifier,
			});
	}

	get currentPriceFilter() {
		return {
			min_price: this.filters.min_price,
			max_price: this.filters.max_price,
		};
	}

	mounted() {
		this.price.value.lower = this.filters.min_price || this.price.min;
		this.price.value.upper = this.filters.max_price || this.price.max;
		this.updateRangeSlider({ lower: this.price.value.lower, upper: this.price.value.upper });
	}

	@Watch('currentPriceFilter')
	changedPriceFilter({ min_price, max_price }: { min_price: number; max_price: number }) {
		this.updateRangeSlider({ lower: min_price || this.price.min, upper: max_price || this.price.max });
	}

	isPriceFilterActive(min: number, max: number) {
		const minPrice = this.filters.min_price;
		const maxPrice = this.filters.max_price;
		return min === minPrice && maxPrice === max;
	}

	setPreset(preset: any) {
		if (this.isPriceFilterActive(preset.min_price, preset.max_price)) {
			this.SET_FILTER({ key: 'min_price', value: null });
			this.SET_FILTER({ key: 'max_price', value: null });
		} else {
			this.SET_FILTER({ key: 'min_price', value: preset.min_price });
			this.SET_FILTER({ key: 'max_price', value: preset.max_price });
		}
	}

	rangeChanged(evt: any) {
		const value = evt.target?.value as { lower: number; upper: number } | undefined;
		// ignore click events outside filter
		if (value === undefined) {
			return;
		}
		this.SET_FILTER({ key: 'min_price', value: value.lower === this.price.min ? null : value.lower });
		this.SET_FILTER({ key: 'max_price', value: value.upper === this.price.max ? null : value.upper });
	}

	private updateRangeSlider({ lower, upper }: { lower: number; upper: number }) {
		(this.$refs['range'] as any).value = {
			lower,
			upper,
		};
	}

	private getPriceLabel(price: number) {
		const priceToString = price.toString();
		return this.isRTL
			? priceToString + this.$t('WEBAPP_STANDARD_CURRENCY')
			: this.$t('WEBAPP_STANDARD_CURRENCY') + priceToString;
	}
}
